import {
  Card,
  Grid,
  MenuItem,
  Icon,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  updateScreenTitle,
  showSuccessMessage,
  showLoader,
  dismissLoader
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import AppService from "../../../../Configurations/Appservice";

// ============================================
// MAIN COMPONENT
// ============================================

const CommissionConfig = () => {
  const dispatch = useDispatch();

  // Channel partner selection
  const [channelPartners, setChannelPartners] = useState([]);
  const [channelPartnersLoading, setChannelPartnersLoading] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  
  // Partner details from API
  const [partnerDetails, setPartnerDetails] = useState(null);
  const [partnerDetailsLoading, setPartnerDetailsLoading] = useState(false);
  const [partnerDetailsError, setPartnerDetailsError] = useState(null);

  // State
  const [activeTab, setActiveTab] = useState(0);

  // Default commission from API
  const [defaultCommission, setDefaultCommission] = useState({ type: '', value: 0 });

  // Dialog states
  const [defaultCommissionDialogOpen, setDefaultCommissionDialogOpen] = useState(false);
  const [defaultCommissionForm, setDefaultCommissionForm] = useState({ type: 'PROFIT', value: '', applyToAll: false });
  const [saving, setSaving] = useState(false);

  // Add Referral Dialog states
  const [addReferralDialogOpen, setAddReferralDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [referralForm, setReferralForm] = useState({
    partnerId: '',
    customerId: '',
    commissionMode: 'default', // 'default' or 'custom'
    commissionPercentage: '',
    commissionType: 'PROFIT'
  });

  // Referred companies state
  const [referredCompanies, setReferredCompanies] = useState([]);
  const [referredCompaniesLoading, setReferredCompaniesLoading] = useState(false);

  // Edit commission dialog state
  const [editCommissionDialogOpen, setEditCommissionDialogOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [editCommissionForm, setEditCommissionForm] = useState({
    commissionPercentage: '',
    commissionType: 'PROFIT'
  });

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    dispatch(updateScreenTitle("Commission Config"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Fetch channel partners list
  useEffect(() => {
    const fetchChannelPartners = async () => {
      setChannelPartnersLoading(true);
      try {
        const response = await AppService.get("v1/organisation/keyValues?type=CHANNEL_PARTNER");
        const list = Array.isArray(response) ? response : response?.data || [];
        setChannelPartners(list);
      } catch (err) {
        console.error("Error fetching channel partners:", err);
        setChannelPartners([]);
      } finally {
        setChannelPartnersLoading(false);
      }
    };
    fetchChannelPartners();
  }, []);

  // Fetch partner details when selected
  useEffect(() => {
    if (selectedPartnerId) {
      fetchPartnerDetails(selectedPartnerId);
      fetchReferredCompanies(selectedPartnerId);
    } else {
      setPartnerDetails(null);
      setDefaultCommission({ type: '', value: 0 });
      setReferredCompanies([]);
    }
  }, [selectedPartnerId]);

  // Fetch referred companies
  const fetchReferredCompanies = async (partnerId) => {
    setReferredCompaniesLoading(true);
    try {
      const response = await AppService.get(`/api/channel-partners/${partnerId}/referred-customers`);
      const list = Array.isArray(response) ? response : response?.data || [];
      setReferredCompanies(list);
    } catch (err) {
      console.error("Error fetching referred companies:", err);
      setReferredCompanies([]);
    } finally {
      setReferredCompaniesLoading(false);
    }
  };

  const fetchPartnerDetails = async (organisationId) => {
    setPartnerDetailsLoading(true);
    setPartnerDetailsError(null);
    try {
      const response = await AppService.get(`/api/channel-partners/onboarding/${organisationId}`);
      const data = response?.data || response;
      setPartnerDetails(data);
      
      // Set default commission from API response
      if (data) {
        setDefaultCommission({
          type: data.commissionType || 'PROFIT',
          value: data.commissionPercentage || 0
        });
      }
    } catch (err) {
      console.error("Error fetching partner details:", err);
      setPartnerDetailsError("Failed to fetch partner details");
      setPartnerDetails(null);
    } finally {
      setPartnerDetailsLoading(false);
    }
  };

  // Get selected partner name
  const getSelectedPartnerName = () => {
    const partner = channelPartners.find(p => p.id === selectedPartnerId);
    return partner?.name || '';
  };

  // Get onboarding ID from partner details
  const getOnboardingId = () => {
    return partnerDetails?.id || partnerDetails?.onboardingId || selectedPartnerId;
  };

  // ============================================
  // HANDLERS
  // ============================================

  const handleEditDefaultCommission = () => {
    setDefaultCommissionForm({
      type: defaultCommission.type || 'PROFIT',
      value: defaultCommission.value || '',
      applyToAll: false
    });
    setDefaultCommissionDialogOpen(true);
  };

  const handleSaveDefaultCommission = async () => {
    const onboardingId = getOnboardingId();
    if (!onboardingId) {
      console.error("No onboarding ID found");
      return;
    }

    setSaving(true);
    dispatch(showLoader());
    try {
      await AppService.put(`/api/channel-partners/onboarding/${onboardingId}/commission-settings`, {
        commissionPercentage: parseFloat(defaultCommissionForm.value),
        commissionType: defaultCommissionForm.type,
        applyToAll: defaultCommissionForm.applyToAll
      });

      // Update local state
      setDefaultCommission({
        type: defaultCommissionForm.type,
        value: parseFloat(defaultCommissionForm.value)
      });

      dispatch(showSuccessMessage('Commission settings updated successfully'));
      setDefaultCommissionDialogOpen(false);
    } catch (err) {
      console.error("Error saving commission settings:", err);
    } finally {
      setSaving(false);
      dispatch(dismissLoader());
    }
  };

  // Fetch customers list
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await AppService.get("v1/organisation/keyValues?type=CUSTOMER");
      const list = Array.isArray(response) ? response : response?.data || [];
      setCustomers(list);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleOpenAddReferral = () => {
    setReferralForm({
      partnerId: selectedPartnerId,
      customerId: '',
      commissionMode: 'default',
      commissionPercentage: '',
      commissionType: 'PROFIT'
    });
    fetchCustomers();
    setAddReferralDialogOpen(true);
  };

  const handleSaveReferral = async () => {
    if (!referralForm.partnerId || !referralForm.customerId) {
      return;
    }

    setSaving(true);
    dispatch(showLoader());
    try {
      const requestBody = {
        customerId: referralForm.customerId,
        commissionPercentage: referralForm.commissionMode === 'custom' ? parseFloat(referralForm.commissionPercentage) : null,
        commissionType: referralForm.commissionMode === 'custom' ? referralForm.commissionType : null
      };

      await AppService.post(`/api/channel-partners/${referralForm.partnerId}/link-customer`, requestBody);

      dispatch(showSuccessMessage('Customer linked successfully'));
      setAddReferralDialogOpen(false);
      // Refresh the list
      fetchReferredCompanies(selectedPartnerId);
    } catch (err) {
      console.error("Error linking customer:", err);
    } finally {
      setSaving(false);
      dispatch(dismissLoader());
    }
  };

  // Handle edit commission click
  const handleEditCommission = (referral) => {
    setSelectedReferral(referral);
    setEditCommissionForm({
      commissionPercentage: referral.commissionPercentage || '',
      commissionType: referral.commissionType || 'PROFIT'
    });
    setEditCommissionDialogOpen(true);
  };

  // Handle save commission update
  const handleSaveCommissionUpdate = async () => {
    if (!selectedReferral || !editCommissionForm.commissionPercentage) {
      return;
    }

    setSaving(true);
    dispatch(showLoader());
    try {
      await AppService.put(
        `/api/channel-partners/referred-customers/${selectedReferral.id}/commission`,
        {
          commissionPercentage: parseFloat(editCommissionForm.commissionPercentage),
          commissionType: editCommissionForm.commissionType
        }
      );

      dispatch(showSuccessMessage('Commission updated successfully'));
      setEditCommissionDialogOpen(false);
      setSelectedReferral(null);
      // Refresh the list
      fetchReferredCompanies(selectedPartnerId);
    } catch (err) {
      console.error("Error updating commission:", err);
    } finally {
      setSaving(false);
      dispatch(dismissLoader());
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  // ============================================
  // RENDER
  // ============================================

  return (
    <WDBox py={1}>
      {/* Channel Partner Selection */}
      <Card sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}>
          Select Channel Partner
        </Typography>
        <FormControl fullWidth sx={{ maxWidth: 400 }}>
          <InputLabel id="channel-partner-select">Channel Partner</InputLabel>
          <Select
            labelId="channel-partner-select"
            value={selectedPartnerId}
            onChange={(e) => setSelectedPartnerId(e.target.value)}
            label="Channel Partner"
            disabled={channelPartnersLoading}
          >
            {channelPartners.map((partner) => (
              <MenuItem key={partner.id} value={partner.id}>
                {partner.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {channelPartnersLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Loading channel partners...</Typography>
          </Box>
        )}
      </Card>

      {/* Partner Info Card - Only show when partner is selected */}
      {selectedPartnerId && (
        <Card sx={{ p: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Icon sx={{ fontSize: 32, color: 'primary.main' }}>business</Icon>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {getSelectedPartnerName()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.813rem' }}>
                Manage commission rates for this channel partner
              </Typography>
            </Box>
          </Box>
          {partnerDetailsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading partner details...</Typography>
            </Box>
          )}
          {partnerDetailsError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {partnerDetailsError}
            </Alert>
          )}
        </Card>
      )}

      {/* Show content only when partner is selected */}
      {selectedPartnerId && !partnerDetailsLoading && (
        <>
          {/* Tabs */}
          <Card sx={{ mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minHeight: 48
                }
              }}
            >
              <Tab label="Default Commission" />
              <Tab label="Referred Companies" />
            </Tabs>
          </Card>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                    Default Commission Rate
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This is the base commission rate applied to all services and companies unless overridden.
                  </Typography>
                </Box>
                <WDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>edit</Icon>}
                  onClick={handleEditDefaultCommission}
                  size="small"
                  sx={{
                    py: 0.75,
                    px: 2,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Edit Commission
                </WDButton>
              </Box>

              <Grid container spacing={3}>
                {/* Commission Percentage */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{
                    p: 3,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    bgcolor: 'primary.lighter',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Commission Percentage
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {defaultCommission.value}%
                    </Typography>
                  </Box>
                </Grid>

                {/* Commission Type */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{
                    p: 3,
                    border: '2px solid',
                    borderColor: defaultCommission.type === 'PROFIT' ? 'success.main' : 'info.main',
                    borderRadius: 2,
                    bgcolor: defaultCommission.type === 'PROFIT' ? 'success.lighter' : 'info.lighter',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Commission Type
                    </Typography>
                    <Typography variant="h4" sx={{ 
                      color: defaultCommission.type === 'PROFIT' ? 'success.main' : 'info.main', 
                      fontWeight: 600 
                    }}>
                      {defaultCommission.type || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {defaultCommission.type === 'PROFIT' 
                        ? 'Commission calculated on profit margin' 
                        : defaultCommission.type === 'REVENUE'
                        ? 'Commission calculated on total revenue'
                        : ''}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          )}

          {activeTab === 1 && (
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                    Referred Companies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Companies referred by this channel partner.
                  </Typography>
                </Box>
                <WDButton
                  variant="gradient"
                  color="success"
                  startIcon={<Icon>add</Icon>}
                  onClick={handleOpenAddReferral}
                  size="small"
                  sx={{
                    py: 0.75,
                    px: 2,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Add Referral
                </WDButton>
              </Box>

              {referredCompaniesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : referredCompanies.length === 0 ? (
                <Alert severity="info" icon={<Icon>info</Icon>}>
                  No companies linked to this partner yet.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#064d51' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Customer Name</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Commission Type</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Commission %</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created At</TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {referredCompanies.map((referral) => (
                        <TableRow key={referral.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {referral.customerName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={referral.status || 'ACTIVE'}
                              size="small"
                              sx={{
                                backgroundColor: referral.status === 'ACTIVE' ? '#4caf50' : '#9e9e9e',
                                color: 'white',
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={referral.commissionType || 'DEFAULT'}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: referral.commissionType === 'PROFIT' ? 'success.main' : 'info.main',
                                color: referral.commissionType === 'PROFIT' ? 'success.main' : 'info.main',
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="primary.main">
                              {referral.commissionPercentage != null ? `${referral.commissionPercentage}%` : 'Default'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(referral.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit Commission">
                              <IconButton
                                size="small"
                                onClick={() => handleEditCommission(referral)}
                                sx={{ color: '#064d51' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          )}
        </>
      )}

      {/* Edit Default Commission Dialog */}
      <Dialog open={defaultCommissionDialogOpen} onClose={() => setDefaultCommissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Default Commission</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Commission Type"
                  value={defaultCommissionForm.type}
                  onChange={(e) => setDefaultCommissionForm({ ...defaultCommissionForm, type: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="PROFIT">PROFIT</MenuItem>
                  <MenuItem value="REVENUE">REVENUE</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Commission Percentage"
                  value={defaultCommissionForm.value}
                  onChange={(e) => setDefaultCommissionForm({ ...defaultCommissionForm, value: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={defaultCommissionForm.applyToAll}
                      onChange={(e) => setDefaultCommissionForm({ ...defaultCommissionForm, applyToAll: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Apply commission settings to all referred channels"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDefaultCommissionDialogOpen(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSaveDefaultCommission} 
            variant="contained" 
            color="primary" 
            disabled={!defaultCommissionForm.value || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Referral Dialog */}
      <Dialog open={addReferralDialogOpen} onClose={() => setAddReferralDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Referral</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Channel Partner Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Channel Partner</InputLabel>
                  <Select
                    value={referralForm.partnerId}
                    onChange={(e) => setReferralForm({ ...referralForm, partnerId: e.target.value })}
                    label="Channel Partner"
                  >
                    {channelPartners.map((partner) => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Customer Selection */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={referralForm.customerId}
                    onChange={(e) => setReferralForm({ ...referralForm, customerId: e.target.value })}
                    label="Customer"
                    disabled={customersLoading}
                  >
                    {customers.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {customersLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">Loading customers...</Typography>
                  </Box>
                )}
              </Grid>

              {/* Commission Mode Selection */}
              <Grid item xs={12}>
                <TextField
                  select
                  label="Commission"
                  value={referralForm.commissionMode}
                  onChange={(e) => setReferralForm({ ...referralForm, commissionMode: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </TextField>
              </Grid>

              {/* Custom Commission Fields - Only show when custom is selected */}
              {referralForm.commissionMode === 'custom' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      type="number"
                      label="Commission Percentage"
                      value={referralForm.commissionPercentage}
                      onChange={(e) => setReferralForm({ ...referralForm, commissionPercentage: e.target.value })}
                      fullWidth
                      InputProps={{
                        endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Commission Type"
                      value={referralForm.commissionType}
                      onChange={(e) => setReferralForm({ ...referralForm, commissionType: e.target.value })}
                      fullWidth
                    >
                      <MenuItem value="PROFIT">PROFIT</MenuItem>
                      <MenuItem value="REVENUE">REVENUE</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddReferralDialogOpen(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSaveReferral} 
            variant="contained" 
            color="primary" 
            disabled={!referralForm.customerId || (referralForm.commissionMode === 'custom' && !referralForm.commissionPercentage) || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Commission Dialog */}
      <Dialog open={editCommissionDialogOpen} onClose={() => setEditCommissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#064d51', fontWeight: 'bold' }}>Update Commission</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedReferral && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Customer: <strong>{selectedReferral.customerName}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Commission: <strong>{selectedReferral.commissionPercentage}% ({selectedReferral.commissionType})</strong>
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Commission Percentage"
                  value={editCommissionForm.commissionPercentage}
                  onChange={(e) => setEditCommissionForm({ ...editCommissionForm, commissionPercentage: e.target.value })}
                  fullWidth
                  InputProps={{
                    endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Commission Type"
                  value={editCommissionForm.commissionType}
                  onChange={(e) => setEditCommissionForm({ ...editCommissionForm, commissionType: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="PROFIT">PROFIT</MenuItem>
                  <MenuItem value="REVENUE">REVENUE</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCommissionDialogOpen(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSaveCommissionUpdate} 
            variant="contained" 
            disabled={!editCommissionForm.commissionPercentage || saving}
            sx={{ bgcolor: '#064d51', '&:hover': { bgcolor: '#043a3e' } }}
          >
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </WDBox>
  );
};

export default CommissionConfig;
